/**
 * @file button 模式面板：overlay 绑定、搜索 dismiss、首库高亮
 */
import { useEffect, useState } from 'react';
import type { IKnowledgeBaseItem } from './useKnowledgeBaseApi';

interface IUseKbPanelChromeParams {
  opened: boolean;
  isAtMode: boolean;
  isButtonMode: boolean;
  regionMountKey: number;
  panelKbList: IKnowledgeBaseItem[];
  kbPanelLoading: boolean;
  debouncedSearchKeyword: string;
  panelSearchInput: string;
  handlePanelSearchChange: (keyword: string) => void;
  activePanelKbId: string;
  setActivePanelKbId: (kbId: string) => void;
  loadKbDocs: (ids: string[]) => void;
  interactedRef: React.MutableRefObject<boolean>;
}

/**
 * button 模式面板 DOM 副作用（文档加载由 index 层 useInteractiveKbDocs 负责）
 */
export const useKbPanelChrome = ({
  opened,
  isAtMode,
  isButtonMode,
  regionMountKey,
  panelKbList,
  kbPanelLoading,
  debouncedSearchKeyword,
  panelSearchInput,
  handlePanelSearchChange,
  activePanelKbId,
  setActivePanelKbId,
  loadKbDocs,
  interactedRef,
}: IUseKbPanelChromeParams) => {
  const [overlayEl, setOverlayEl] = useState<HTMLElement | null>(null);

  useEffect(() => {
    if (!opened || isAtMode) {
      setOverlayEl(null);
      return undefined;
    }
    let raf = 0;
    const bind = () => {
      const el = document.querySelector<HTMLElement>('.dba-kb-popover-overlay');
      if (el) {
        setOverlayEl(el);
        return;
      }
      raf = window.requestAnimationFrame(bind);
    };
    bind();
    return () => {
      window.cancelAnimationFrame(raf);
      setOverlayEl(null);
    };
  }, [opened, isAtMode, regionMountKey]);

  useEffect(() => {
    if (!opened) {
      setActivePanelKbId('');
      return;
    }
    if (isAtMode || debouncedSearchKeyword) {
      return;
    }
    setActivePanelKbId('');
  }, [opened, isAtMode, debouncedSearchKeyword, setActivePanelKbId]);

  useEffect(() => {
    if (!overlayEl || !isButtonMode) {
      return undefined;
    }
    const iconWrap = overlayEl.querySelector('.dbc-region-select-search-icon');
    if (!iconWrap) {
      return undefined;
    }
    const handleDismissCapture = (e: MouseEvent): void => {
      if (!panelSearchInput.trim()) {
        return;
      }
      const target = e.target as HTMLElement | null;
      if (!target || !iconWrap.contains(target)) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      handlePanelSearchChange('');
    };
    iconWrap.addEventListener('click', handleDismissCapture, true);
    return () => {
      iconWrap.removeEventListener('click', handleDismissCapture, true);
    };
  }, [overlayEl, isButtonMode, panelSearchInput, handlePanelSearchChange, regionMountKey]);

  useEffect(() => {
    if (!opened || isAtMode || kbPanelLoading || !panelKbList.length) {
      return;
    }
    const kbIds = panelKbList.map((kb) => String(kb.kbId));
    const firstKbId = kbIds[0];
    if (debouncedSearchKeyword) {
      setActivePanelKbId(firstKbId);
      loadKbDocs(kbIds);
      return;
    }
    if (!activePanelKbId) {
      setActivePanelKbId(firstKbId);
      loadKbDocs([firstKbId]);
    }
  }, [
    opened,
    isAtMode,
    kbPanelLoading,
    panelKbList,
    debouncedSearchKeyword,
    activePanelKbId,
    loadKbDocs,
    setActivePanelKbId,
  ]);

  useEffect(() => {
    if (!overlayEl) {
      return undefined;
    }
    const onDown = () => {
      interactedRef.current = true;
    };
    overlayEl.addEventListener('mousedown', onDown);
    return () => overlayEl.removeEventListener('mousedown', onDown);
  }, [overlayEl, interactedRef]);

  return { overlayEl };
};
